import type { CareReminderStatus, CareReminderView } from './types/care.types';
import { conversationTitle, messagePreview, unreadCount, type Conversation } from '../chat/chat.api';

const TERMINAIS: CareReminderStatus[] = ['DONE', 'NOT_INDICATED', 'DECLINED'] as CareReminderStatus[];

export function lembreteEstaAberto(l: Pick<CareReminderView, 'status' | 'nextReminderDueAt' | 'lastAnswerOptionId'>, agora: Date = new Date()): boolean {
  if (TERMINAIS.includes(l.status)) return false;
  if (!l.lastAnswerOptionId) return true;
  if (!l.nextReminderDueAt) return false;
  return new Date(l.nextReminderDueAt) <= agora;
}

export function lembretesAbertos(lembretes: CareReminderView[], agora: Date = new Date()): CareReminderView[] {
  return lembretes.filter((l) => lembreteEstaAberto(l, agora));
}

export interface Atencao {
  chat: boolean;
  cuidado: boolean;
}

export function contarNaoLidas(conversas: any[], userId?: string): number {
  if (!userId) return 0;
  return conversas.reduce((total, c) => {
    if (typeof c?.computedUnread === 'number') return total + (c.computedUnread > 0 ? 1 : 0);
    const lido = c?.participantState?.[userId]?.lastReadSeq ?? 0;
    return total + ((c?.seq ?? 0) > lido ? 1 : 0);
  }, 0);
}

/**
 * `cuidado` acende com lembrete aberto ou com checkpoint pendente. O
 * checkpoint entrou em 10/10/2026, quando o banner "Seu cuidado de hoje"
 * saiu da home: era ele que avisava do checkpoint fora da aba Cuidado, e o
 * aviso passou para a bolinha do coração — fixa como era o banner, que
 * nenhuma marca consegue tirar.
 */
export function avaliarAtencao(
  conversas: any[],
  lembretes: CareReminderView[],
  userId?: string,
  agora: Date = new Date(),
  pendentes: Array<{ type: string; alreadyAnswered?: boolean }> = [],
): Atencao {
  const checkpointPendente = pendentes.some((i) => i.type === 'CHECKPOINT' && !i.alreadyAnswered);
  return { chat: contarNaoLidas(conversas, userId) > 0, cuidado: checkpointPendente || lembretesAbertos(lembretes, agora).length > 0 };
}

/**
 * Uma notificação do sino do cabeçalho (14/10/2026, item 9 da design).
 *
 * Ainda não existe central de notificações guardada (com lida/não lida por
 * item): o sino mostra o que já está esperando a pessoa, das mesmas fontes
 * que acendem as bolinhas das abas — mensagem não lida, lembrete aberto,
 * checkpoint pendente. Some quando ela resolve, não quando abre o sino.
 */
export interface NotificacaoDaPaciente {
  chave: string;
  tipo: 'CHAT' | 'LEMBRETE' | 'CHECKPOINT';
  titulo: string;
  texto: string;
  /** Quando aconteceu, se houver: a última mensagem, o lembrete que venceu. */
  quando: string | null;
  rota: string;
  /** Quanto soma no selo: as mensagens não lidas da conversa; 1 nos demais. */
  quantidade: number;
}

export function notificacoesDaPaciente(
  conversas: Conversation[],
  lembretes: CareReminderView[],
  userId?: string,
  agora: Date = new Date(),
  pendentes: Array<{ id: string; type: string; title: string; description?: string | null; alreadyAnswered?: boolean }> = [],
): NotificacaoDaPaciente[] {
  const chat: NotificacaoDaPaciente[] = userId
    ? conversas
        .map((c) => ({ c, naoLidas: unreadCount(c, userId) }))
        .filter(({ naoLidas }) => naoLidas > 0)
        .map(({ c, naoLidas }) => ({
          chave: `chat:${c.id}`,
          tipo: 'CHAT' as const,
          titulo: c.canal?.nome ?? conversationTitle(c, userId),
          texto: messagePreview(c.lastMessage) || (naoLidas > 1 ? `${naoLidas} mensagens novas` : 'Mensagem nova'),
          quando: c.lastMessage?.createdAt ?? c.updatedAt ?? null,
          rota: `/eu/chat/${c.id}`,
          quantidade: naoLidas,
        }))
    : [];
  const doCuidado: NotificacaoDaPaciente[] = [
    ...pendentes
      .filter((i) => i.type === 'CHECKPOINT' && !i.alreadyAnswered)
      .map((i) => ({ chave: `checkpoint:${i.id}`, tipo: 'CHECKPOINT' as const, titulo: i.title, texto: i.description?.trim() || 'Responda quando puder.', quando: null, rota: `/eu/care-item/${i.id}`, quantidade: 1 })),
    ...lembretesAbertos(lembretes, agora).map((l) => ({
      chave: `lembrete:${l.id}`,
      tipo: 'LEMBRETE' as const,
      titulo: l.title,
      texto: l.description?.trim() || 'Lembrete de cuidado',
      quando: l.nextReminderDueAt,
      rota: '/eu/care-reminders',
      quantidade: 1,
    })),
  ];
  // As mensagens primeiro, da mais recente; o cuidado depois, na ordem em que chega.
  chat.sort((a, b) => new Date(b.quando ?? 0).getTime() - new Date(a.quando ?? 0).getTime());
  return [...chat, ...doCuidado];
}
